import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Switch,
  FormControlLabel,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Autocomplete,
  Grid,
  Avatar
} from '@mui/material';
import { UploadSimple, Trash, Calendar } from '@phosphor-icons/react';
import api from '../../utils/api';
import { getAvatarSrc, getStaffMemberDisplayPhoto } from '../../utils/avatarConstants';
import { useBusiness } from '../../context/BusinessContext';
import ViewAllShiftsDialog from './ViewAllShiftsDialog';

const NONE_MEMBER_OPTION = { _id: '__none__', isNoneOption: true, label: 'None (no linked user)' };

function EditStaffModal({ open, onClose, staffMember, onStaffUpdated, businessId, onStaffDeleted }) {
  const { selectedBusiness } = useBusiness();
  const [formData, setFormData] = useState({
    name: '',
    role: '',
    profileImage: '',
    showOnProfile: false,
    notes: '',
    selectedBusinessMember: null
  });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  const [businessMembers, setBusinessMembers] = useState([NONE_MEMBER_OPTION]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [viewShiftsOpen, setViewShiftsOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const effectiveBusinessId = businessId || selectedBusiness?.business?._id;

  // Fetch BusinessMembers when modal opens
  useEffect(() => {
    if (open && effectiveBusinessId) {
      fetchBusinessMembers();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- verified intentional deps
  }, [open, effectiveBusinessId]);

  useEffect(() => {
    if (staffMember) {
      const currentBusinessMemberId = staffMember.businessMember?._id || staffMember.businessMember;
      let selectedMember = null;
      
      // If businessMembers are already loaded, find the matching member
      if (currentBusinessMemberId && businessMembers.length > 0) {
        selectedMember = businessMembers.find(m => {
          const memberId = m._id?.toString();
          const bmId = currentBusinessMemberId.toString();
          return memberId === bmId || memberId === (currentBusinessMemberId._id?.toString() || bmId);
        }) || null;
      }
      
      // If businessMember is already a populated object, use it directly
      if (!selectedMember && staffMember.businessMember && typeof staffMember.businessMember === 'object' && staffMember.businessMember._id) {
        selectedMember = staffMember.businessMember;
      }
      
      setFormData({
        name: staffMember.name || '',
        role: staffMember.role || '',
        profileImage: staffMember.profileImage || '',
        showOnProfile: staffMember.showOnProfile || false,
        notes: staffMember.notes || '',
        selectedBusinessMember: selectedMember
      });
    }
  }, [staffMember, businessMembers]);

  // Reset form when modal closes
  useEffect(() => {
    if (!open) {
      setFormData({
        name: '',
        role: '',
        profileImage: '',
        showOnProfile: false,
        notes: '',
        selectedBusinessMember: null
      });
      setError('');
    }
  }, [open]);

  const fetchBusinessMembers = async () => {
    if (!effectiveBusinessId) return;
    
    setLoadingMembers(true);
    try {
      const response = await api.get(`/api/business-members/business/${effectiveBusinessId}`);
      const members = response.data.members || [];
      
      // Get current staff member's businessMember ID (could be ObjectId string or populated object)
      const currentBusinessMemberId = staffMember?.businessMember?._id || staffMember?.businessMember;
      
      // Filter out members that already have StaffMember records (except current one)
      const staffResponse = await api.get(`/api/supplier/staff-members?businessId=${effectiveBusinessId}`);
      const existingStaffMemberIds = new Set(
        (staffResponse.data.staffMembers || [])
          .filter(sm => sm.businessMember && sm._id !== staffMember?._id)
          .map(sm => {
            const bmId = sm.businessMember._id || sm.businessMember;
            return bmId?.toString();
          })
      );
      
      // Only show members that don't already have StaffMember records (or current one)
      const availableMembers = members.filter(m => {
        const memberId = m._id?.toString();
        const isCurrentLinkedMember = currentBusinessMemberId && 
          (memberId === currentBusinessMemberId.toString() || 
           memberId === (currentBusinessMemberId._id?.toString() || currentBusinessMemberId.toString()));
        return !existingStaffMemberIds.has(memberId) || isCurrentLinkedMember;
      });
      
      const validMembers = availableMembers.filter(m => m?.user || m?.invitedEmail);
      setBusinessMembers([NONE_MEMBER_OPTION, ...validMembers]);
    } catch (err) {
      // Don't show error - just continue without dropdown
      setBusinessMembers([NONE_MEMBER_OPTION]);
    } finally {
      setLoadingMembers(false);
    }
  };

  const handleBusinessMemberSelect = (member) => {
    if (member && !member.isNoneOption) {
      const userName = `${member.user?.firstName || ''} ${member.user?.lastName || ''}`.trim();
      setFormData(prev => ({
        ...prev,
        selectedBusinessMember: member,
        name: userName || prev.name,
        role: member.role || prev.role, // Auto-populate role from BusinessMember
        profileImage: member.user ? '' : prev.profileImage
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        selectedBusinessMember: null,
        role: '', // Clear role when deselecting
        profileImage: staffMember?.profileImage || ''
      }));
    }
  };

  const handleChange = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedMimeTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    const fileName = file.name || '';
    const isAllowedExt = /\.(jpe?g|png)$/i.test(fileName);
    const isAllowedMimeType = allowedMimeTypes.includes(file.type);

    if (!isAllowedMimeType && !isAllowedExt) {
      setError('Only PNG or JPEG images can be uploaded');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError('Image size must be less than 5MB');
      return;
    }

    setUploading(true);
    setError('');

    try {
      const formDataToUpload = new FormData();
      formDataToUpload.append('headshot', file);

      const response = await api.post('/api/supplier/upload-team-member-headshot', formDataToUpload, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setFormData(prev => ({ ...prev, profileImage: response.data.headshot }));
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to upload image. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      setError('Name is required');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const hasLinkedPlatformUser = !!(formData.selectedBusinessMember?.user || staffMember?.user);

      const updateData = {
        name: formData.name.trim(),
        role: formData.selectedBusinessMember ? undefined : (formData.role.trim() || undefined), // Don't send role if linked to BusinessMember
        showOnProfile: formData.showOnProfile,
        notes: formData.notes.trim() || undefined
      };
      if (!hasLinkedPlatformUser) {
        updateData.profileImage = formData.profileImage || undefined;
      }

      // Handle linking/unlinking
      const currentBusinessMemberId = staffMember?.businessMember?._id || staffMember?.businessMember;
      const newBusinessMemberId = formData.selectedBusinessMember?._id || null;
      
      if (currentBusinessMemberId !== newBusinessMemberId) {
        updateData.linkToBusinessMemberId = newBusinessMemberId;
      }

      const response = await api.put(`/api/supplier/staff-members/${staffMember._id}`, updateData);

      onStaffUpdated(response.data.staffMember);
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update staff member. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    setDeleteConfirmOpen(false);
    setDeleting(true);
    setError('');

    try {
      await api.delete(`/api/supplier/staff-members/${staffMember._id}`);
      onStaffDeleted?.(staffMember._id);
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete staff member. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteClick = () => {
    setDeleteConfirmOpen(true);
  };

  const handleClose = () => {
    if (!saving && !deleting) {
      setError('');
      onClose();
    }
  };

  if (!staffMember) {
    return null;
  }

  const hasLinkedPlatformUser = !!(formData.selectedBusinessMember?.user || staffMember?.user);
  const linkedProfilePreview = getStaffMemberDisplayPhoto({
    user: formData.selectedBusinessMember?.user || staffMember?.user,
    profileImage: staffMember?.profileImage
  });

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Staff Member</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          {error && (
            <Alert severity="error" onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          <Autocomplete
            options={businessMembers}
            getOptionLabel={(option) => {
              if (option?.isNoneOption) return option.label;
              if (!option.user) return option.invitedEmail || 'Unknown';
              return `${option.user.firstName || ''} ${option.user.lastName || ''}`.trim() || option.user.email || 'Unknown';
            }}
            value={formData.selectedBusinessMember || NONE_MEMBER_OPTION}
            onChange={(_, newValue) => handleBusinessMemberSelect(newValue)}
            loading={loadingMembers}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Link to existing user (Optional)"
                placeholder="Select a user from Users & Access"
                helperText="Select a user who already has platform access to link them"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingMembers ? <CircularProgress size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  )
                }}
              />
            )}
            renderOption={(props, option) => {
              if (option?.isNoneOption) {
                return (
                  <Box component="li" {...props}>
                    <Typography variant="body1">{option.label}</Typography>
                  </Box>
                );
              }

              const userName = option.user 
                ? `${option.user.firstName || ''} ${option.user.lastName || ''}`.trim() || option.user.email
                : option.invitedEmail || 'Unknown';
              return (
                <Box component="li" {...props}>
                  <Stack>
                    <Typography variant="body1">{userName}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.user?.email || option.invitedEmail} • Role: {option.role}
                    </Typography>
                  </Stack>
                </Box>
              );
            }}
            disabled={saving || deleting}
            isOptionEqualToValue={(option, value) => option?._id === value?._id}
          />

          <TextField
            label="Name"
            required
            fullWidth
            value={formData.name}
            onChange={handleChange('name')}
            disabled={saving || deleting || !!formData.selectedBusinessMember}
            helperText={formData.selectedBusinessMember ? 'Name is set from linked account' : undefined}
          />

          {!formData.selectedBusinessMember && (
            <TextField
              label="Role"
              fullWidth
              value={formData.role}
              onChange={handleChange('role')}
              placeholder="e.g., Photographer, Bartender"
              disabled={saving || deleting}
            />
          )}

          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Profile image {hasLinkedPlatformUser ? '' : '(optional)'}
            </Typography>
            <Grid container spacing={2} alignItems="flex-start">
              <Grid item xs={6}>
                {hasLinkedPlatformUser ? (
                  <Stack spacing={1} alignItems="flex-start">
                    <Avatar
                      src={getAvatarSrc(linkedProfilePreview)}
                      alt=""
                      sx={{ width: 80, height: 80, fontSize: '1.25rem' }}
                    >
                      {(formData.name || '?').charAt(0)}
                    </Avatar>
                    <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 280 }}>
                      Photo comes from their account (Account Settings). They can change it there.
                    </Typography>
                  </Stack>
                ) : formData.profileImage ? (
                  <Stack spacing={1}>
                    <img
                      src={formData.profileImage}
                      alt="Profile preview"
                      style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 8 }}
                    />
                    <input
                      accept=".png,.jpg,.jpeg,image/png,image/jpeg"
                      style={{ display: 'none' }}
                      id="edit-profile-image-upload"
                      type="file"
                      onChange={handleImageUpload}
                      disabled={uploading || saving}
                    />
                    <label htmlFor="edit-profile-image-upload">
                      <Button
                        variant="outlined"
                        component="span"
                        startIcon={<UploadSimple size={16} />}
                        disabled={uploading || saving}
                        fullWidth
                        size="small"
                      >
                        {uploading ? 'Uploading...' : 'Change image'}
                      </Button>
                    </label>
                  </Stack>
                ) : (
                  <>
                    <input
                      accept=".png,.jpg,.jpeg,image/png,image/jpeg"
                      style={{ display: 'none' }}
                      id="edit-profile-image-upload"
                      type="file"
                      onChange={handleImageUpload}
                      disabled={uploading || saving}
                    />
                    <label htmlFor="edit-profile-image-upload">
                      <Button
                        variant="outlined"
                        component="span"
                        startIcon={<UploadSimple size={16} />}
                        disabled={uploading || saving}
                        fullWidth
                      >
                        {uploading ? 'Uploading...' : 'Upload image'}
                      </Button>
                    </label>
                  </>
                )}
              </Grid>
              <Grid item xs={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.showOnProfile}
                      onChange={handleChange('showOnProfile')}
                      disabled={saving}
                    />
                  }
                  label="Show on public profile"
                />
              </Grid>
            </Grid>
          </Box>

          <TextField
            label="Notes"
            fullWidth
            multiline
            rows={3}
            value={formData.notes}
            onChange={handleChange('notes')}
            placeholder="Internal notes about this staff member..."
            disabled={saving}
          />

          <Box sx={{ pt: 1 }}>
            <Button
              variant="outlined"
              startIcon={<Calendar size={16} />}
              onClick={() => setViewShiftsOpen(true)}
              fullWidth
            >
              View All Shifts
            </Button>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Box sx={{ flex: 1 }}>
          {onStaffDeleted && (
            <Button
              onClick={handleDeleteClick}
              color="error"
              disabled={saving || deleting || uploading}
              startIcon={deleting ? <CircularProgress size={16} /> : <Trash size={16} />}
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </Button>
          )}
        </Box>
        <Button onClick={handleClose} disabled={saving || deleting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={saving || deleting || uploading || !formData.name.trim()}
          startIcon={saving ? <CircularProgress size={16} /> : null}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>

      <ViewAllShiftsDialog
        open={viewShiftsOpen}
        onClose={() => setViewShiftsOpen(false)}
        staffMember={staffMember}
        businessId={effectiveBusinessId}
      />

      <Dialog
        open={deleteConfirmOpen}
        onClose={() => !deleting && setDeleteConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete team member?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            This will permanently delete {staffMember?.name || 'this team member'} and remove their schedules and shifts.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDeleteConfirm}
            disabled={deleting}
          >
            {deleting ? 'Deleting...' : 'Delete team member'}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}

export default EditStaffModal;

